import React, { useEffect, useState, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Package, GraduationCap, Bus, Coffee, ChevronDown, X, Heart, Shield, BookOpen, Users, Globe, Camera, Star, Award, Crown } from 'lucide-react';
// --- Atoms ---
const OrnateDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center justify-center gap-3 ${className}`} data-id="element-7564">
    <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/60" data-id="element-7565" />
    <svg width="28" height="14" viewBox="0 0 28 14" fill="none" data-id="element-7566">
      <path d="M14 0 L17 4 L24 5 L19 9 L20 14 L14 11 L8 14 L9 9 L4 5 L11 4 Z" fill="#D4AF37" data-id="element-7567" />
    </svg>
    <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/60" data-id="element-7568" />
  </div>;
const CornerOrnaments = () => <>
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-7569" />
    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-7570" />
    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-7571" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-7572" />
  </>;
const SectionHeader = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="text-center mb-16" data-id="element-7573">
    <OrnateDivider className="mb-6" data-id="element-7574" />
    <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7575">
      {title}
    </h2>
    {subtitle && <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-7576">{subtitle}</p>}
  </div>;
// --- Modals ---
const BuyChaaModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [amount, setAmount] = useState<string>('21');
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 2500);
    }, 1500);
  };
  return <AnimatePresence data-id="element-7577">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-7578">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-7579" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl" data-id="element-7580">
            <CornerOrnaments data-id="element-7581" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-7582">
              <X className="w-5 h-5" data-id="element-7583" />
            </button>

            {isSuccess ? <div className="text-center py-8" data-id="element-7584">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-7585">
                  <Coffee className="w-8 h-8 text-[#D4AF37]" data-id="element-7586" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7587">
                  Thank You
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7588">
                  Your support means the world to our team.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-7589">
                <div className="text-center mb-8" data-id="element-7590">
                  <Coffee className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" data-id="element-7591" />
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7592">
                    Buy Chaa for the Team
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7593">
                    Support the artists, guides, and builders bringing the
                    Lahore Treasury to life.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-7594">
                  {['11', '21', '51'].map(val => <button key={val} type="button" onClick={() => {
              setAmount(val);
              setCustomAmount('');
            }} className={`py-3 border text-sm transition-colors ${amount === val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-7595">
                      ${val}
                    </button>)}
                </div>
                <div className="mb-6" data-id="element-7596">
                  <input type="number" placeholder="Custom Amount ($)" value={customAmount} onChange={e => {
              setCustomAmount(e.target.value);
              setAmount('custom');
            }} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7597" />
                </div>
                <div className="mb-8" data-id="element-7598">
                  <textarea placeholder="Optional message to the team..." value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-7599" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-7600">
                  {isSubmitting ? 'Processing...' : `Support with $${amount === 'custom' ? customAmount || '0' : amount}`}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
const SponsorInquiryModal = ({
  isOpen,
  onClose,
  defaultType = ''
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: string;
}) => {
  const [type, setType] = useState(defaultType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setType(defaultType);
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, defaultType]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 3000);
    }, 1500);
  };
  return <AnimatePresence data-id="element-7601">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-7602">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-7603" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-lg bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-hide" data-id="element-7604">
            <CornerOrnaments data-id="element-7605" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-7606">
              <X className="w-5 h-5" data-id="element-7607" />
            </button>

            {isSuccess ? <div className="text-center py-12" data-id="element-7608">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-7609">
                  <Shield className="w-8 h-8 text-[#D4AF37]" data-id="element-7610" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7611">
                  Inquiry Received
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7612">
                  A member of the Lahore Treasury team will contact you shortly.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-7613">
                <div className="text-center mb-8" data-id="element-7614">
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7615">
                    Become a Patron
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7616">
                    Help open the Lahore Court to the next generation.
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-7617">
                  <input required type="text" placeholder="Full Name" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7618" />
                  <input required type="email" placeholder="Email Address" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7619" />

                  <select value={type} onChange={e => setType(e.target.value)} required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-7620">
                    <option value="" disabled data-id="element-7621">
                      Select Sponsorship Type
                    </option>
                    <option value="Student Access" data-id="element-7622">
                      Student Access ($25+)
                    </option>
                    <option value="School Bus" data-id="element-7623">School Bus ($1,500+)</option>
                    <option value="Collection" data-id="element-7624">
                      Collection Sponsor ($5,000+)
                    </option>
                    <option value="Treasury Night" data-id="element-7625">
                      Treasury Night Sponsor
                    </option>
                    <option value="Commemorative Coin" data-id="element-7626">
                      Commemorative Coin Sponsor
                    </option>
                    <option value="Founding Partner" data-id="element-7627">
                      Founding Partner ($10,000+)
                    </option>
                    <option value="Court Packet" data-id="element-7628">Buy Court Packets</option>
                    <option value="Other" data-id="element-7629">Other Contribution</option>
                  </select>

                  <select required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-7630">
                    <option value="" disabled data-id="element-7631">
                      Budget Range
                    </option>
                    <option value="Under $500" data-id="element-7632">Under $500</option>
                    <option value="$500 - $2,500" data-id="element-7633">$500 - $2,500</option>
                    <option value="$2,500 - $10,000" data-id="element-7634">$2,500 - $10,000</option>
                    <option value="$10,000+" data-id="element-7635">$10,000+</option>
                  </select>

                  <textarea placeholder="Message or specific requests..." className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-7636" />
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-7637">
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
// --- Main Page Component ---
export const SupportTheCourtPage = () => {
  const [isChaaModalOpen, setIsChaaModalOpen] = useState(false);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [sponsorModalType, setSponsorModalType] = useState('');
  const openSponsorModal = (type: string) => {
    setSponsorModalType(type);
    setIsSponsorModalOpen(true);
  };
  // Impact Calculator State
  const [impactAmount, setImpactAmount] = useState<number>(25);
  const [customImpact, setCustomImpact] = useState<string>('');
  const calculateImpact = (amount: number) => {
    const students = Math.floor(amount / 25);
    const packets = students;
    const buses = Math.floor(amount / 2000);
    return {
      students,
      packets,
      buses
    };
  };
  const currentImpact = calculateImpact(impactAmount);
  return <div className="min-h-screen w-full bg-[#0B0F19] text-[#FDFBF7] font-sans selection:bg-[#D4AF37] selection:text-[#0B0F19]" data-id="element-7638">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-7639">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-7640">
          <Link to="/treasure" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-7641">
            <ChevronLeft className="w-4 h-4" data-id="element-7642" /> Treasury Home
          </Link>
          <div className="font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase text-center" data-id="element-7643">
            Support the Court
          </div>
          <div className="w-24" data-id="element-7644" />
        </div>
      </header>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-32 px-4 overflow-hidden" data-id="element-7645">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-7646" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-7647">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-7648">
            <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-6" data-id="element-7649">
              Lahore Treasury
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl text-[#FDFBF7] mb-8 leading-tight" data-id="element-7650">
              Help Open the <br className="hidden sm:block" data-id="element-7651" />
              <span className="text-[#D4AF37]" data-id="element-7652">Lahore Court</span>
            </h1>
            <p className="text-[#FDFBF7]/70 text-lg max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-7653">
              Lahore Treasury is more than a collectible platform. It is a
              cultural experience designed to bring the world of Maharaja Ranjit
              Singh’s Lahore Court to families, students, schools, and the next
              generation.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20" data-id="element-7654">
              <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-7655">
                Sponsor a Student
              </button>
              <button onClick={() => openSponsorModal('School Bus')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7656">
                Sponsor a Bus
              </button>
              <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-7657">
                Become a Patron
              </button>
            </div>

            {/* Hero Visual: Elegant Sealed Packet */}
            <div className="relative w-48 h-64 mx-auto group" data-id="element-7658">
              <div className="absolute inset-0 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_0_40px_rgba(212,175,55,0.1)] group-hover:border-[#D4AF37] group-hover:shadow-[0_0_60px_rgba(212,175,55,0.2)] transition-all duration-700 flex items-center justify-center" data-id="element-7659">
                <CornerOrnaments data-id="element-7660" />
                <div className="w-12 h-12 rounded-full bg-[#8B0000] border-2 border-[#D4AF37] flex items-center justify-center shadow-lg relative z-10" data-id="element-7661">
                  <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-7662" />
                </div>
                <div className="absolute top-1/2 left-0 w-full h-px bg-[#D4AF37]/30 -translate-y-1/2" data-id="element-7663" />
                <div className="absolute left-1/2 top-0 w-px h-full bg-[#D4AF37]/30 -translate-x-1/2" data-id="element-7664" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Quick Support Options */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7665">
        <div className="max-w-6xl mx-auto" data-id="element-7666">
          <SectionHeader title="Choose How to Support" data-id="element-7667" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7668">
            {[{
            title: 'Buy a Court Packet',
            price: 'From $14.99',
            icon: Package,
            copy: 'Open a premium digital packet and reveal portraits, seals, treasures, or memory fragments from the Lahore Treasury.',
            bullets: ['3 collectible reveals', 'Chance of Rare, Epic, or Treasury Rare', 'Adds to your personal collection', 'Includes lore and historical notes'],
            cta: 'Buy Court Packet',
            action: () => openSponsorModal('Court Packet')
          }, {
            title: 'Sponsor a Student',
            price: '$25',
            icon: GraduationCap,
            copy: 'Help a young person experience The Kingdoms of Punjab and receive their first Lahore Treasury collectible.',
            bullets: ['Supports student access', 'Includes one digital Court Packet', 'Optional patron wall recognition', 'Ideal for families and alumni'],
            cta: 'Sponsor a Student',
            action: () => openSponsorModal('Student Access')
          }, {
            title: 'Sponsor a School Bus',
            price: '$1,500–$2,500',
            icon: Bus,
            copy: 'Bring an entire group of students to the live experience and give them a cultural memory they can keep.',
            bullets: ['Supports up to 50 students', 'Includes group Court Packet unlock', 'Recognition as a Bus Patron', 'Optional school dedication'],
            cta: 'Sponsor a Bus',
            action: () => openSponsorModal('School Bus')
          }, {
            title: 'Buy Chaa for the Team',
            price: '$11 / $21 / $51',
            icon: Coffee,
            copy: 'Support the artists, guides, volunteers, and builders bringing the Lahore Treasury to life.',
            bullets: ['Small patron gesture', 'Helps support production days', 'Unlocks a thank-you badge', 'Optional message to the team'],
            cta: 'Buy Chaa',
            action: () => setIsChaaModalOpen(true)
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-[#0B0F19] border border-[#D4AF37]/20 p-8 hover:border-[#D4AF37]/60 transition-all duration-300 flex flex-col" data-id="element-7669">
                <div className="w-12 h-12 rounded-full bg-[#131B2B] border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-7670">
                  <card.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-7671" />
                </div>
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-7672">
                  {card.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-7673">
                  {card.price}
                </p>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 leading-relaxed" data-id="element-7674">
                  {card.copy}
                </p>
                <ul className="space-y-3 mb-8 flex-1" data-id="element-7675">
                  {card.bullets.map((b, j) => <li key={j} className="flex items-start gap-2 text-xs text-[#FDFBF7]/50" data-id="element-7676">
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-1.5 shrink-0" data-id="element-7677" />
                      {b}
                    </li>)}
                </ul>
                <button onClick={card.action} className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-7678">
                  {card.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 3. Impact Calculator */}
      <section className="py-24 px-4 relative" data-id="element-7679">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-7680">
          <div className="bg-[#131B2B] border border-[#D4AF37]/30 p-8 sm:p-12 relative overflow-hidden" data-id="element-7681">
            <CornerOrnaments data-id="element-7682" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.1),_transparent_70%)]" data-id="element-7683" />

            <div className="text-center mb-10" data-id="element-7684">
              <h2 className="font-serif text-3xl text-[#FDFBF7] mb-3" data-id="element-7685">
                Build Your Impact
              </h2>
              <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7686">
                Choose a contribution and see what it helps unlock.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-7687">
              {[{
              label: '1 Student',
              val: 25
            }, {
              label: '10 Students',
              val: 250
            }, {
              label: '1 School Bus',
              val: 2000
            }, {
              label: 'Chaa',
              val: 21
            }].map(opt => <button key={opt.val} onClick={() => {
              setImpactAmount(opt.val);
              setCustomImpact('');
            }} className={`px-6 py-3 border text-xs tracking-wider uppercase transition-colors ${impactAmount === opt.val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-7688">
                  {opt.label} (${opt.val})
                </button>)}
              <div className="relative" data-id="element-7689">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FDFBF7]/40 text-sm" data-id="element-7690">
                  $
                </span>
                <input type="number" placeholder="Custom" value={customImpact} onChange={e => {
                setCustomImpact(e.target.value);
                setImpactAmount(Number(e.target.value) || 0);
              }} className="w-32 bg-[#0B0F19] border border-[#D4AF37]/20 pl-8 pr-4 py-3 text-xs text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors uppercase tracking-wider" data-id="element-7691" />
              </div>
            </div>

            <div className="text-center mb-10" data-id="element-7692">
              <p className="text-xl sm:text-2xl font-serif text-[#FDFBF7] leading-relaxed" data-id="element-7693">
                Your support helps bring{' '}
                <span className="text-[#D4AF37] text-3xl sm:text-4xl mx-2" data-id="element-7694">
                  {currentImpact.students}
                </span>{' '}
                students into the Lahore Court experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-[#D4AF37]/15" data-id="element-7695">
              <div className="text-center" data-id="element-7696">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7697">
                  {currentImpact.students}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7698">
                  Students Supported
                </p>
              </div>
              <div className="text-center" data-id="element-7699">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7700">
                  {currentImpact.packets}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7701">
                  Packets Unlocked
                </p>
              </div>
              <div className="text-center" data-id="element-7702">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7703">
                  {currentImpact.buses > 0 ? currentImpact.buses : '< 1'}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7704">
                  Buses Funded
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Sponsor a Collection */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7705">
        <div className="max-w-6xl mx-auto" data-id="element-7706">
          <SectionHeader title="Sponsor a Collection" subtitle="Help preserve and present one part of the Lahore Treasury." data-id="element-7707" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-7708">
            {[{
            name: 'Lahore Court Collection',
            desc: 'Founding faces of sovereignty.',
            price: '$5,000',
            sponsoredBy: 'Akal Steel',
            isFlagship: true
          }, {
            name: 'Founders’ Seals',
            desc: 'Authority made visible.',
            price: '$7,500'
          }, {
            name: 'Court Treasures',
            desc: 'Jewels, regalia, and royal objects.',
            price: '$10,000'
          }, {
            name: 'Royal Women & Chamber Culture',
            desc: 'The inner court of counsel, lineage, music, and refinement.',
            price: '$7,500'
          }, {
            name: 'Sacred Authority',
            desc: 'Reverence before rule.',
            price: '$10,000'
          }, {
            name: 'Frontier Defenders',
            desc: 'Martial prestige at the empire’s edge.',
            price: '$7,500'
          }].map((col: any, i: number) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`group relative bg-[#0B0F19] border p-8 transition-all duration-300 ${col.sponsoredBy ? 'border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.08)]' : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/60'}`} data-id="element-7709">
                {col.sponsoredBy && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-7710" />}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.05),_transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7711" />
                {col.isFlagship && <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase font-bold mb-2 relative z-10" data-id="element-7712">
                    Flagship Collection
                  </p>}
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 relative z-10" data-id="element-7713">
                  {col.name}
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 h-10 relative z-10" data-id="element-7714">
                  {col.desc}
                </p>
                {col.sponsoredBy ? <>
                    <div className="pt-6 border-t border-[#D4AF37]/30 mb-6 relative z-10" data-id="element-7715">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1.5" data-id="element-7716">
                        Presented in partnership with
                      </p>
                      <p className="font-serif text-xl text-[#D4AF37] leading-none" data-id="element-7717">
                        {col.sponsoredBy}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] mt-2 italic" data-id="element-7718">
                        Sponsorship secured.
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#D4AF37]/8 border border-[#D4AF37]/40 text-[#D4AF37]/85 text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/15 transition-colors relative z-10" data-id="element-7719">
                      Inquire About Other Tiers
                    </button>
                  </> : <>
                    <div className="pt-6 border-t border-[#D4AF37]/15 mb-6 relative z-10" data-id="element-7720">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1" data-id="element-7721">
                        Starting From
                      </p>
                      <p className="text-[#D4AF37] font-mono text-lg" data-id="element-7722">
                        {col.price}
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#131B2B] border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors relative z-10" data-id="element-7723">
                      Become a Patron
                    </button>
                  </>}
              </motion.div>)}
          </div>
          <p className="text-center text-[#FDFBF7]/30 text-[10px] max-w-2xl mx-auto uppercase tracking-wider" data-id="element-7724">
            Collection sponsors may receive recognition on the website, event
            materials, patron wall, and selected digital collectible pages.
            Final recognition is subject to cultural and editorial approval.
          </p>
        </div>
      </section>

      {/* 5. Patron Tiers */}
      <section className="py-24 px-4" data-id="element-7725">
        <div className="max-w-6xl mx-auto" data-id="element-7726">
          <SectionHeader title="Patron Tiers" subtitle="For families, businesses, foundations, and community leaders who want to help build the Lahore Treasury." data-id="element-7727" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7728">
            {[{
            tier: 'Friend of the Court',
            price: '$101',
            benefits: ['Name on digital patron wall', '1 supporter badge', '1 Court Packet'],
            cta: 'Become a Friend',
            type: 'Other'
          }, {
            tier: 'Darbar Patron',
            price: '$501',
            benefits: ['Patron wall recognition', '5 Court Packets', 'Early access to selected drops', 'Invitation to patron preview'],
            cta: 'Become a Patron',
            type: 'Other'
          }, {
            tier: 'Treasury Patron',
            price: '$2,500',
            benefits: ['Sponsor student access', '15 Court Packets', 'Recognition on selected collection page', 'Optional family dedication'],
            cta: 'Become a Treasury Patron',
            type: 'Other'
          }, {
            tier: 'Founding Patron',
            price: '$10,000+',
            benefits: ['Major recognition', 'Collection sponsorship eligibility', 'Private preview experience', 'Founder’s Edition digital collectible', 'Optional commemorative coin eligibility'],
            cta: 'Apply as Founding Patron',
            type: 'Founding Partner',
            isPremium: true
          }].map((tier, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative p-8 flex flex-col ${tier.isPremium ? 'bg-gradient-to-b from-[#131B2B] to-[#0B0F19] border border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'bg-[#131B2B]/40 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors'}`} data-id="element-7729">
                {tier.isPremium && <CornerOrnaments data-id="element-7730" />}
                <h3 className={`font-serif text-xl mb-2 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`} data-id="element-7731">
                  {tier.tier}
                </h3>
                <p className="font-mono text-lg text-[#FDFBF7]/80 mb-6 pb-6 border-b border-[#D4AF37]/15" data-id="element-7732">
                  {tier.price}
                </p>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-7733">
                  {tier.benefits.map((b, j) => <li key={j} className="flex items-start gap-3 text-sm text-[#FDFBF7]/70" data-id="element-7734">
                      <Shield className={`w-4 h-4 shrink-0 mt-0.5 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#D4AF37]/50'}`} data-id="element-7735" />
                      <span className="leading-tight" data-id="element-7736">{b}</span>
                    </li>)}
                </ul>
                <button onClick={() => openSponsorModal(tier.type)} className={`w-full py-3 text-[10px] tracking-[0.2em] uppercase transition-colors ${tier.isPremium ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] font-bold' : 'border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19]'}`} data-id="element-7737">
                  {tier.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. Where Your Support Goes */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7738">
        <div className="max-w-5xl mx-auto" data-id="element-7739">
          <SectionHeader title="Where Your Support Goes" data-id="element-7740" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-7741">
            {[{
            icon: Users,
            label: 'Student Access'
          }, {
            icon: Bus,
            label: 'School Buses'
          }, {
            icon: Camera,
            label: 'Artists & Designers'
          }, {
            icon: BookOpen,
            label: 'Historical Research'
          }, {
            icon: Globe,
            label: 'Live Experience Production'
          }, {
            icon: Package,
            label: 'Digital Collectible Platform'
          }, {
            icon: Heart,
            label: 'Community Programming'
          }, {
            icon: Shield,
            label: 'Preservation of Heritage'
          }].map((item, i) => <div key={i} className="flex flex-col items-center" data-id="element-7742">
                <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center mb-4" data-id="element-7743">
                  <item.icon className="w-5 h-5 text-[#D4AF37]/70" data-id="element-7744" />
                </div>
                <p className="text-[#FDFBF7]/80 text-xs uppercase tracking-wider leading-relaxed max-w-[120px]" data-id="element-7745">
                  {item.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Sponsor Opportunities */}
      <section className="py-24 px-4" data-id="element-7746">
        <div className="max-w-6xl mx-auto" data-id="element-7747">
          <div className="text-center mb-16" data-id="element-7748">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7749">
              For Sponsors
            </h2>
            <p className="text-[#D4AF37] text-lg font-serif italic mb-4" data-id="element-7750">
              Sponsor a cultural moment, not just a logo placement.
            </p>
            <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-7751">
              Lahore Treasury gives sponsors the ability to support student
              access, live cultural programming, digital collectibles,
              commemorative rewards, and community recognition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7752">
            {[{
            title: 'Student Access Sponsor',
            price: 'Custom',
            bestFor: 'Families, Foundations'
          }, {
            title: 'School Bus Sponsor',
            price: 'From $1,500',
            bestFor: 'Businesses, Community Groups'
          }, {
            title: 'Collection Sponsor',
            price: 'From $5,000',
            bestFor: 'Families, Businesses'
          }, {
            title: 'Treasury Night Sponsor',
            price: 'Custom',
            bestFor: 'Corporate Sponsors'
          }, {
            title: 'Commemorative Coin Sponsor',
            price: 'Custom',
            bestFor: 'Elected Officials, Businesses'
          }, {
            title: 'Founding Partner',
            price: '$10,000+',
            bestFor: 'Major Philanthropists'
          }].map((opp, i) => <div key={i} className="bg-[#131B2B]/60 border border-[#D4AF37]/20 p-6 flex flex-col hover:border-[#D4AF37]/50 transition-colors" data-id="element-7753">
                <h3 className="font-serif text-lg text-[#FDFBF7] mb-1" data-id="element-7754">
                  {opp.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-7755">
                  {opp.price}
                </p>
                <div className="mt-auto pt-4 border-t border-[#D4AF37]/10 mb-6" data-id="element-7756">
                  <p className="text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase mb-1" data-id="element-7757">
                    Best For
                  </p>
                  <p className="text-[#FDFBF7]/80 text-xs" data-id="element-7758">{opp.bestFor}</p>
                </div>
                <button onClick={() => openSponsorModal(opp.title)} className="w-full py-2.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-7759">
                  Request Sponsor Info
                </button>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. FAQ Section */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7760">
        <div className="max-w-3xl mx-auto" data-id="element-7761">
          <SectionHeader title="Questions Before You Support?" data-id="element-7762" />
          <div className="space-y-4" data-id="element-7763">
            {[{
            q: 'Is this an NFT project?',
            a: 'No. Lahore Treasury uses digital collectibles for cultural storytelling, event access, recognition, and collection-building. It should not be presented as a crypto or investment product.'
          }, {
            q: 'Can I sponsor a specific school?',
            a: 'Yes, where operationally possible. Sponsors can request a school, region, or community group.'
          }, {
            q: 'Can my family or business be recognized?',
            a: 'Yes. Recognition options may include the patron wall, selected collection pages, event materials, or sponsor acknowledgements, subject to cultural and editorial approval.'
          }, {
            q: 'Can I make a custom contribution?',
            a: 'Yes. Custom support is available for patrons, sponsors, families, and foundations.'
          }, {
            q: 'What does "Buy Chaa for the Team" mean?',
            a: 'It is a small contribution that supports the artists, guides, volunteers, and builders working behind the scenes.'
          }].map((faq, i) => {
            const [isOpen, setIsOpen] = useState(false);
            return <div key={i} className="border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-7764">
                  <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-7765">
                    <span className="font-serif text-lg text-[#FDFBF7] pr-4" data-id="element-7766">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7767" />
                  </button>
                  <AnimatePresence data-id="element-7768">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-7769">
                        <div className="px-6 pb-5 text-[#FDFBF7]/60 text-sm leading-relaxed border-t border-[#D4AF37]/10 pt-4" data-id="element-7770">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* 9. Final CTA */}
      <section className="py-32 px-4 relative overflow-hidden" data-id="element-7771">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-7772" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-7773">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7774">
            Help Open the Lahore Court
          </h2>
          <p className="text-[#FDFBF7]/60 text-lg mb-12" data-id="element-7775">
            Support a student, fund a bus, sponsor a collection, or become a
            patron of the Treasury.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7776">
            <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-7777">
              Sponsor a Student
            </button>
            <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7778">
              Become a Patron
            </button>
            <button onClick={() => openSponsorModal('Other')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-7779">
              Request Sponsor Info
            </button>
          </div>
        </div>
      </section>

      {/* 10. Footer */}
      <footer className="px-4 py-16 bg-[#0B0F19] border-t border-[#D4AF37]/20 text-center" data-id="element-7780">
        <div className="max-w-4xl mx-auto" data-id="element-7781">
          <OrnateDivider className="mb-10" data-id="element-7782" />

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-12" data-id="element-7783">
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7784">
              Treasury Home
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7785">
              Collections
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7786">
              Court Packet
            </Link>
            <Link to="/support-the-court" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7787">
              Support the Court
            </Link>
            <a href="#" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7788">
              Contact
            </a>
          </div>

          <p className="text-[#D4AF37] font-serif text-xl tracking-[0.3em] uppercase mb-3" data-id="element-7789">
            Lahore Treasury
          </p>
          <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase font-light" data-id="element-7790">
            Enter the Lahore Court
          </p>
        </div>
      </footer>

      {/* Modals Mounted at Root */}
      <BuyChaaModal isOpen={isChaaModalOpen} onClose={() => setIsChaaModalOpen(false)} data-id="element-7791" />
      <SponsorInquiryModal isOpen={isSponsorModalOpen} onClose={() => setIsSponsorModalOpen(false)} defaultType={sponsorModalType} data-id="element-7792" />
    </div>;
};